import { useCallback, useEffect, useRef, useState } from "react";
import { transcribe, warmUp } from "services/sttService";
import { getLanguageName } from "constants/languages";

/**
 * Record a clinical encounter on ANY browser, by transcribing server-side.
 *
 * WHY THIS EXISTS. useVoiceRecording uses the Web Speech API, which is Chrome-only: it refuses
 * Brave and Edge outright, and on iOS every browser is WebKit, where continuous recognition
 * drops words at every restart seam. That made "record on your phone" an Android-Chrome-only
 * feature and left Brave/Edge desktop users unable to record at all.
 *
 * Web Speech also relays the audio to Google's speech servers, which are NOT covered by our
 * BAA. Patient audio is PHI. CareStart was deliberately moved off Gemini Live for exactly that
 * reason (see functions/src/chartmind/stt.js), and Twilio features across this repo sit
 * disabled pending a BAA. ChartMind was the outlier.
 *
 * ⚠️ API-COMPATIBLE WITH useVoiceRecording, ON PURPOSE. Same options, same return shape, so a
 * call site swaps one import and nothing else. Both hooks are kept so a bad day is a one-line
 * revert rather than an archaeology exercise.
 *
 * ⚠️ WHAT IS GENUINELY DIFFERENT, and you should expect to notice it. Web Speech streams
 * interim results word by word. This transcribes in segments, so text lands in chunks a few
 * seconds behind the speaker instead of appearing as it is spoken. That is the cost of working
 * everywhere and of keeping audio on a BAA-covered subprocessor. Accuracy should be better.
 *
 * HOW SEGMENTS ARE CUT. An RMS meter off an AnalyserNode watches for silence, the same
 * technique (and the same thresholds) CareStart's voice engine uses to end a patient's turn.
 * A segment closes on a real pause, but only once it is worth transcribing, and it is forced
 * closed if the speaker never pauses. Those two bounds are what keep this from either firing a
 * call per breath or holding a whole encounter in one request.
 *
 * ⚠️ EACH SEGMENT IS ITS OWN MediaRecorder, and that is not incidental. Only the FIRST chunk a
 * recorder emits carries the container header, so `start(timeslice)` chunks are not
 * independently decodable and the transcriber rejects everything after the first. The recorder
 * therefore stops and restarts per segment, which CareStart also does (per turn). The gap that
 * costs us lands in the silence we cut on, which is why we cut on silence.
 */

/** Container the browser can record and the transcriber accepts. Copied from CareStart's engine. */
function pickRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
}

// Voice-activity thresholds on the smoothed 0..1 RMS level. Same values as CareStart's engine —
// copied rather than imported, because that engine is half-duplex and coupled to a brain and a
// TTS pipeline, and 30 lines of duplicated constants is a far better trade than surgery on a
// live patient-facing voice engine.
const SPEECH_LEVEL_THRESHOLD = 0.045;
const SILENCE_LEVEL_THRESHOLD = 0.03;
const MIC_LEVEL_SMOOTHING = 0.7;
const MIC_LEVEL_SENSITIVITY = 6;
const LEVEL_POLL_MS = 80;

/**
 * A pause this long, after speech, closes a segment.
 *
 * CareStart uses 1500ms because it is deciding whether a PATIENT has finished answering, and
 * cutting a patient off mid-thought is rude. Here nothing is waiting on the boundary — the next
 * segment starts immediately — so the only thing a long wait buys is latency.
 */
const SEGMENT_SILENCE_MS = 800;

/**
 * Floor on segment length.
 *
 * ⚠️ THIS NUMBER IS THE LATENCY, so treat changes to it as a UX decision and not a tuning
 * detail. Time to first visible word is roughly MIN_SEGMENT_MS + SEGMENT_SILENCE_MS + the
 * round trip. At 5000/1500 that was about ten seconds and read as broken; at 2500/1500 it was
 * still five and a half, which is what got it called insane. At 1500/800 it is a bit over two
 * seconds plus the round trip.
 *
 * Going lower trades accuracy: the transcriber has less context per request, which costs most
 * on exactly the medical terms that matter. Below about a second it also stops being worth the
 * per-request overhead. This is the floor of what is useful, not a starting point for more.
 */
const MIN_SEGMENT_MS = 1_500;

/**
 * Ceiling on segment length, for a speaker who never pauses. Also bounds how much audio a
 * single failure can lose, and keeps each request far inside the transcriber's limits.
 *
 * It is also the wait before the first words of a non-stop speaker appear: at 25s that read as
 * broken. Shorter costs next to nothing, since the transcriber bills by audio length, not by
 * request; the price is the odd word split across two segments.
 */
const MAX_SEGMENT_MS = 10_000;

/**
 * Below this a segment is a cough or a door, not speech.
 *
 * Raised well above CareStart's 1200: CareStart's floor guards a deliberate patient turn, where
 * anything recorded was probably an answer. Here segments are cut automatically every second or
 * two, so plenty of them contain no speech at all — and near-silence is precisely what makes a
 * Whisper-family model invent a sentence in another language. Discarding quiet segments outright
 * is both cheaper and more accurate than transcribing them and cleaning up after.
 */
const MIN_SEGMENT_BLOB_BYTES = 6_000;

/** Attempts per segment before its audio is given up on. */
const MAX_SEGMENT_ATTEMPTS = 3;

const LOG_PREFIX = "[useEncounterRecording]";

/**
 * Scripts a segment must not be written in when the encounter is in a Latin-script language.
 *
 * ⚠️ WHY THIS GUARD EXISTS. Server-side transcription detects the language per request, and a
 * request here is one short segment. A segment that is mostly noise — a cough, a chair, a door,
 * two words at the edge of audible — is exactly the input Whisper-family models hallucinate on,
 * and the hallucination is very often a plausible-looking phrase in an unrelated language. The
 * result was English encounters with stray Hebrew and Korean in them, which then made the
 * clarifying-questions model answer in Hebrew.
 *
 * A language hint is already sent (the callable narrows BCP-47 to ISO-639-1 and passes it), but
 * it is a hint and not a pin, deliberately: pinning would break a genuinely multilingual
 * patient. So the hint stays soft and this catches what gets through.
 *
 * Dropping the text rather than keeping it is the right way round. This is a clinical record,
 * every downstream step reads it as fact, and a hallucinated sentence is worse than a missing
 * one — it was never said by anybody.
 */
// Devanagari's combining marks make this class match half a grapheme, which is what trips
// no-misleading-character-class. That is exactly what is wanted here: the ratio in
// looksLikeWrongScript counts CODEPOINTS that are not Latin, and a combining mark is as good a
// signal of a misdetected script as the base letter it sits on. Nothing ever slices or
// reconstructs the text off this match, so half a grapheme cannot corrupt anything.
// eslint-disable-next-line no-misleading-character-class
const NON_LATIN_SCRIPT = /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0900-\u097F\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uAC00-\uD7AF\u0400-\u04FF]/g;

/** Languages ChartMind offers that are written in Latin script. */
const LATIN_SCRIPT_PRIMARIES = new Set(["en", "sw", "fr", "es", "pt", "de", "it", "nl", "id"]);

/** Letters only, so punctuation and digits do not dilute the ratio. */
const LETTERS = /[\p{L}]/gu;

/**
 * True when `text` looks like a language misdetection for an encounter being conducted in
 * `language`. Threshold rather than "any", so a single quoted foreign word survives.
 */
function looksLikeWrongScript(text, language) {
  const primary = String(language || "").toLowerCase().split(/[-_]/)[0];
  if (!LATIN_SCRIPT_PRIMARIES.has(primary)) return false;
  const letters = (text.match(LETTERS) || []).length;
  if (letters < 4) return false;
  const foreign = (text.match(NON_LATIN_SCRIPT) || []).length;
  return foreign / letters > 0.3;
}

/**
 * No browser-brand check, deliberately.
 *
 * voiceConversationFlags.js says it outright for this pipeline: "Do not reintroduce a
 * browser-brand check for the OpenAI pipeline." MediaRecorder plus getUserMedia is the real
 * capability test, and it passes on Safari, Firefox, Brave and Edge.
 */
const checkSupport = () =>
  typeof window !== "undefined" &&
  typeof MediaRecorder !== "undefined" &&
  Boolean(navigator?.mediaDevices?.getUserMedia) &&
  Boolean(pickRecorderMimeType());

const useEncounterRecording = ({ language = "en-US" } = {}) => {
  const [isSupported] = useState(() => checkSupport());
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);
  /** Segments recorded but not yet transcribed. Surfaced so a UI can say "still catching up". */
  const [pendingSegments, setPendingSegments] = useState(0);
  /** Smoothed 0..1 input level, so a UI can show that the mic is live between segments. */
  const [micLevel, setMicLevel] = useState(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const analyserBufRef = useRef(null);
  const levelPollRef = useRef(null);
  const timerRef = useRef(null);

  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);
  const micLevelRef = useRef(0);
  const segmentStartedAtRef = useRef(0);
  const hasSpokenRef = useRef(false);
  const lastSpeechAtRef = useRef(0);
  const cuttingRef = useRef(false);

  /**
   * The transcript, assembled in order.
   *
   * Segments are transcribed concurrently but must be appended in the order they were spoken,
   * or a clinician gets their encounter shuffled. Each segment claims an index on the way in
   * and writes into this sparse array; the visible transcript is the filled prefix plus
   * whatever has landed after it, so a slow segment delays only its own text.
   */
  const segmentTextsRef = useRef([]);
  const nextSegmentIndexRef = useRef(0);

  /**
   * Bumped whenever the transcript is replaced wholesale — a new recording, a reset, or text
   * set by hand.
   *
   * ⚠️ WHY A GENERATION COUNTER AND NOT JUST CLEARING THE ARRAY. Clearing it does nothing about
   * a segment already out for transcription: when that request resolves it writes its index
   * back in, so audio from the previous encounter reappends itself under the new text. That is
   * a wrong-encounter bug in a clinical record, and it is invisible — the words look plausible.
   * A segment that returns from a superseded generation is discarded instead.
   */
  const generationRef = useRef(0);
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  /** Text set by hand (paste, session restore) always sorts before recorded segments. */
  const manualPrefixRef = useRef("");

  const composeTranscript = useCallback(() => {
    const spoken = segmentTextsRef.current
      .filter((t) => typeof t === "string" && t.length > 0)
      .join(" ");
    const parts = [manualPrefixRef.current, spoken].filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }, []);

  const publishTranscript = useCallback(() => {
    setTranscript(composeTranscript());
  }, [composeTranscript]);

  const transcribeSegment = useCallback(
    async (blob, index, generation) => {
      setPendingSegments((n) => n + 1);
      try {
        for (let attempt = 1; attempt <= MAX_SEGMENT_ATTEMPTS; attempt += 1) {
          try {
            const text = await transcribe(blob, {
              language: languageRef.current,
              // Names the language from ChartMind's selector rather than assuming English, so
              // a Swahili or French encounter is pinned to its own language just as firmly.
              // The `language` code above is only a soft hint server-side; this is the lever
              // that actually holds it. See the comment in functions/src/chartmind/stt.js.
              prompt: `This is a medical consultation spoken in ${getLanguageName(
                languageRef.current,
              )}. Transcribe it in ${getLanguageName(languageRef.current)}.`,
            });
            if (generation !== generationRef.current) return; // superseded; drop it
            if (looksLikeWrongScript(text, languageRef.current)) {
              console.warn(
                `${LOG_PREFIX} Dropped segment ${index}: transcribed in the wrong script`,
                { language: languageRef.current, text },
              );
              segmentTextsRef.current[index] = "";
              publishTranscript();
              return;
            }
            segmentTextsRef.current[index] = text || "";
            publishTranscript();
            return;
          } catch (err) {
            if (generation !== generationRef.current) return; // superseded; stop retrying
            if (attempt === MAX_SEGMENT_ATTEMPTS) {
              // Losing a segment silently would put a hole in a clinical record that reads as
              // fact downstream, so say so rather than quietly dropping it.
              console.error(`${LOG_PREFIX} Segment ${index} failed:`, err?.message);
              segmentTextsRef.current[index] = "";
              publishTranscript();
              setError(
                "Part of the recording could not be transcribed. Please review the transcript before continuing.",
              );
              return;
            }
            // Usually a blip on clinic wifi. Back off and try the same audio again.
            await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          }
        }
      } finally {
        setPendingSegments((n) => Math.max(0, n - 1));
      }
    },
    [publishTranscript],
  );

  /** Assemble what the recorder produced and hand it off. */
  const handleSegmentBlob = useCallback(() => {
    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (!chunks.length) return;

    const blob = new Blob(chunks, { type: recorderRef.current?.mimeType || "audio/webm" });
    if (blob.size < MIN_SEGMENT_BLOB_BYTES) return;

    const index = nextSegmentIndexRef.current;
    nextSegmentIndexRef.current += 1;
    segmentTextsRef.current[index] = undefined;
    void transcribeSegment(blob, index, generationRef.current);
  }, [transcribeSegment]);

  /** Build a recorder for one segment and start it. */
  const startSegmentRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;

    const mimeType = pickRecorderMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      handleSegmentBlob();
      // Chain the next segment here rather than at the call site: onstop is the only point at
      // which the previous recorder has actually surrendered the stream.
      if (isRecordingRef.current && !isPausedRef.current) {
        startSegmentRecorder();
      }
      cuttingRef.current = false;
    };

    // No timeslice: one blob per segment, so every blob carries its own container header.
    recorder.start();
    segmentStartedAtRef.current = Date.now();
    hasSpokenRef.current = false;
    lastSpeechAtRef.current = Date.now();
  }, [handleSegmentBlob]);

  /** Close the current segment. The recorder's onstop starts the next one. */
  const cutSegment = useCallback(() => {
    if (cuttingRef.current) return;
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    cuttingRef.current = true;
    recorder.stop();
  }, []);

  const stopLevelPoll = useCallback(() => {
    if (levelPollRef.current) {
      clearInterval(levelPollRef.current);
      levelPollRef.current = null;
    }
  }, []);

  /** One loop drives the level meter and the segment boundaries. */
  const startLevelPoll = useCallback(() => {
    stopLevelPoll();
    levelPollRef.current = setInterval(() => {
      const analyser = analyserRef.current;
      const buf = analyserBufRef.current;
      if (!analyser || !buf) return;
      if (!isRecordingRef.current || isPausedRef.current) return;

      analyser.getFloatTimeDomainData(buf);
      let sumSq = 0;
      for (let i = 0; i < buf.length; i += 1) sumSq += buf[i] * buf[i];
      const rms = Math.sqrt(sumSq / buf.length);
      const boosted = Math.min(1, rms * MIC_LEVEL_SENSITIVITY);
      micLevelRef.current =
        micLevelRef.current * MIC_LEVEL_SMOOTHING + boosted * (1 - MIC_LEVEL_SMOOTHING);
      const level = micLevelRef.current;
      setMicLevel(level);

      const now = Date.now();
      const segmentAge = now - segmentStartedAtRef.current;

      if (level >= SPEECH_LEVEL_THRESHOLD) {
        hasSpokenRef.current = true;
        lastSpeechAtRef.current = now;
      }

      // A speaker who never pauses still gets their transcript in pieces.
      if (segmentAge >= MAX_SEGMENT_MS) {
        cutSegment();
        return;
      }

      // A real pause, but only once the segment is long enough to be worth a request.
      if (
        hasSpokenRef.current &&
        segmentAge >= MIN_SEGMENT_MS &&
        level < SILENCE_LEVEL_THRESHOLD &&
        now - lastSpeechAtRef.current >= SEGMENT_SILENCE_MS
      ) {
        cutSegment();
      }
    }, LEVEL_POLL_MS);
  }, [stopLevelPoll, cutSegment]);

  const teardownAudio = useCallback(() => {
    stopLevelPoll();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      analyserRef.current?.disconnect();
    } catch {
      // Already gone.
    }
    analyserRef.current = null;
    analyserBufRef.current = null;
    const ctx = audioContextRef.current;
    audioContextRef.current = null;
    if (ctx) ctx.close().catch(() => {});
    const stream = streamRef.current;
    streamRef.current = null;
    stream?.getTracks().forEach((track) => track.stop());
  }, [stopLevelPoll]);

  /**
   * Warm the transcription function on mount.
   *
   * Fire-and-forget, and it never throws. Without it the first segment of a session pays a
   * Cloud Function cold start on top of everything else, which is the single worst-feeling
   * moment of a recording: it is the one the clinician is watching to decide whether this
   * works at all. CareStartPage does the same thing on mount for the same reason.
   */
  useEffect(() => {
    warmUp();
  }, []);

  useEffect(() => () => {
    isRecordingRef.current = false;
    try {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    } catch {
      // Nothing to do on unmount.
    }
    teardownAudio();
  }, [teardownAudio]);

  /**
   * Start capturing.
   *
   * ⚠️ RESOLVES TRUE ONLY ONCE THE MICROPHONE IS ACTUALLY OPEN, and that is load-bearing for
   * callers. The permission prompt is inside this call, so it can sit unanswered for as long
   * as the person takes. A caller that flips its UI into a recording state before this
   * resolves is claiming to record while a prompt is still on screen, and whatever it put
   * under the user's thumb in the meantime (a Stop button, say) is live during the prompt.
   * Wait for the answer, then change the UI.
   *
   * @returns {Promise<boolean>} whether capture is running. False is not an exception: the
   *   hook has already set `error` with something the caller can show as-is.
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError("This browser cannot record audio. Please try a different browser.");
      return false;
    }
    if (isRecordingRef.current) return true;

    try {
      setError(null);
      setTranscript("");
      setRecordingTime(0);
      setPendingSegments(0);
      generationRef.current += 1;
      segmentTextsRef.current = [];
      nextSegmentIndexRef.current = 0;
      manualPrefixRef.current = "";
      micLevelRef.current = 0;
      setMicLevel(0);
      cuttingRef.current = false;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioContextRef.current = ctx;
      // Safari starts contexts suspended until a gesture; this call is inside the click.
      await ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.2;
      source.connect(analyser);
      analyserRef.current = analyser;
      analyserBufRef.current = new Float32Array(analyser.fftSize);

      isRecordingRef.current = true;
      setIsRecording(true);
      setIsPaused(false);

      startSegmentRecorder();
      startLevelPoll();
      timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
      return true;
    } catch (err) {
      console.error(`${LOG_PREFIX} Failed to start:`, err);
      isRecordingRef.current = false;
      setIsRecording(false);
      teardownAudio();
      setError(
        err?.name === "NotAllowedError"
          ? "Microphone permission denied. Please allow microphone access."
          : `Could not start recording: ${err?.message || "unknown error"}`,
      );
      return false;
    }
  }, [isSupported, startSegmentRecorder, startLevelPoll, teardownAudio]);

  /**
   * Stop, and wait for the audio already recorded to come back as text.
   *
   * ⚠️ AWAITING MATTERS HERE. Unlike Web Speech, whose text is already in hand when you stop,
   * the last segment of this one is still in flight — and on ChartMind's Record step, stopping
   * is immediately followed by reading the transcript to run the differential. Returning early
   * would run it against an encounter missing its final seconds.
   */
  const stopRecording = useCallback(async () => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;

    const recorder = recorderRef.current;
    if (recorder?.state === "recording") {
      const flushed = new Promise((resolve) => {
        recorder.addEventListener("stop", () => resolve(), { once: true });
      });
      recorder.stop();
      await flushed;
    }

    teardownAudio();
    setIsRecording(false);
    setIsPaused(false);

    // Let the queued segments land. Bounded, so a dead network cannot hang the UI forever; the
    // per-segment retries above have already had their turn by then.
    const deadline = Date.now() + 30_000;
    while (
      segmentTextsRef.current.some((t) => t === undefined) &&
      Date.now() < deadline
    ) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    publishTranscript();
  }, [teardownAudio, publishTranscript]);

  const pauseRecording = useCallback(() => {
    if (!isRecordingRef.current || isPausedRef.current) return;
    isPausedRef.current = true;
    setIsPaused(true);
    // Cut rather than MediaRecorder.pause(): a paused recorder resumes into the same blob, and
    // Safari's support for pause/resume is not something to lean on.
    cutSegment();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [cutSegment]);

  const resumeRecording = useCallback(() => {
    if (!isRecordingRef.current || !isPausedRef.current) return;
    isPausedRef.current = false;
    setIsPaused(false);
    startSegmentRecorder();
    timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
  }, [startSegmentRecorder]);

  const resetRecording = useCallback(() => {
    setTranscript("");
    setRecordingTime(0);
    setError(null);
    setPendingSegments(0);
    generationRef.current += 1;
    segmentTextsRef.current = [];
    nextSegmentIndexRef.current = 0;
    manualPrefixRef.current = "";
  }, []);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Set the transcript by hand (paste, or restoring a saved session).
   *
   * Recorded segments are cleared: the incoming text is now the whole truth, and leaving them
   * would have a later segment append itself onto pasted text it has nothing to do with.
   */
  const updateTranscript = useCallback((newTranscript) => {
    generationRef.current += 1;
    manualPrefixRef.current = newTranscript || "";
    segmentTextsRef.current = [];
    nextSegmentIndexRef.current = 0;
    setTranscript(newTranscript || "");
  }, []);

  /** The authoritative transcript, for callers reading it right after stopRecording(). */
  const getFinalTranscript = useCallback(() => composeTranscript(), [composeTranscript]);

  return {
    isSupported,
    isRecording,
    isPaused,
    recordingTime,
    transcript,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    clearError,
    updateTranscript,
    getFinalTranscript,
    // Beyond useVoiceRecording's shape. pendingSegments distinguishes "quiet" from "still
    // catching up"; micLevel lets a UI prove the mic is live in the seconds before the first
    // text lands, which is the whole complaint about segment-based transcription.
    pendingSegments,
    micLevel,
  };
};

export default useEncounterRecording;
